import React from 'react';

/**
 * Utility functions to prevent translation of specific elements
 */

/**
 * Adds translation-resistant attributes to an element
 * @param element The element to make translation-resistant
 */
export const makeTranslationResistant = (element: HTMLElement): void => {
  if (!element) return;

  // Add class
  element.classList.add('translation-resistant');

  // Add attributes
  element.setAttribute('translate', 'no');
  element.setAttribute('data-translate', 'no');
};

/**
 * Creates a ref callback that makes an element translation-resistant
 * @returns A ref callback function
 */
export const useTranslationResistantRef = () => {
  return (element: HTMLElement | null) => {
    if (element) {
      makeTranslationResistant(element);
    }
  };
};

/**
 * Higher-order component to make a component translation-resistant
 * @param Component The component to wrap
 * @returns A wrapped component with translation resistance
 */
export const withTranslationResistance = <P extends Record<string, unknown>>(
  Component: React.ComponentType<P>,
): React.FC<P> => {
  const WithTranslationResistance = (props: P) => {
    return React.createElement(
      'div',
      {
        className: 'translation-resistant',
        translate: 'no',
        'data-translate': 'no',
      },
      React.createElement(Component, props),
    );
  };

  WithTranslationResistance.displayName = `WithTranslationResistance(${
    Component.displayName || Component.name || 'Component'
  })`;

  return WithTranslationResistance;
};
